import useSWR from 'swr';
import qs from 'qs';
import type { AxiosResponse } from 'axios';

import request from '@/utils/request';
import type * as Type from '@/common/interface';
import {
  assertCompleteZipBlob,
  assertZipContentType,
} from '@/utils/zipArchive';

export type CodexTerminalItem = {
  id: number;
  device_code: string;
  computer_name: string;
  cpu_model: string;
  memory_bytes: number;
  os_version: string;
  app_version: string;
  codex_version: string;
  direct_ip: string;
  log_mode: number;
  online: boolean;
  first_registered_at: number;
  last_seen_at: number;
  last_log_uploaded_at: number;
  last_localization: string;
  last_client_error: string;
};

export type CodexTerminalLogItem = {
  id: number;
  terminal_id: number;
  period_start: number;
  period_end: number;
  level: string;
  source: string;
  line_count: number;
  size_bytes: number;
  sha256: string;
  upload_ip: string;
  created_at: number;
};

export type CodexTerminalLocalizationDeltaItem = {
  id: number;
  app_version: string;
  codex_version: string;
  label_key: string;
  label_value: string;
  source: string;
  source_log_id: number;
  last_seen_at: number;
  created_at: number;
  updated_at: number;
};

export type CodexTerminalListParams = {
  page: number;
  page_size: number;
  query?: string;
  online?: string;
  log_mode?: number;
};

export type CodexTerminalLocalizationDeltaListParams = {
  page: number;
  page_size: number;
  query?: string;
  app_version?: string;
};

export type CodexTerminalLogPolicy = {
  log_mode: number;
};

export type CloudCodexTestAccessConfig = {
  schema_version: 1;
  testing_enabled: boolean;
  user_ids: string[];
};

export type CodexTerminalRecentLogArchiveParams = {
  from: number;
  to: number;
  app_version?: string;
  platform?: 'windows' | 'macos';
  nsis_only?: boolean;
};

export type CodexTerminalRecentLogArchiveJob = {
  job_id: string;
  status: 'preparing' | 'ready' | 'failed' | 'expired';
  filename?: string;
  size_bytes?: number;
  sha256?: string;
  log_count?: number;
  message?: string;
  expires_at?: number;
};

export type CodexTerminalLocalizationDeltaScanResp = {
  hours: number;
  upserted: number;
};

export const useCodexTerminals = (params: CodexTerminalListParams) => {
  const apiUrl = `/answer/admin/api/codex-terminals?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<CodexTerminalItem>,
    Error
  >([apiUrl], request.instance.get);
  return { data, error, mutate, isLoading: !data && !error };
};

export const useCodexTerminalLocalizationDeltas = (
  params: CodexTerminalLocalizationDeltaListParams,
) => {
  const apiUrl = `/answer/admin/api/codex-terminals/localization-deltas?${qs.stringify(
    params,
    {
      skipNulls: true,
    },
  )}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<CodexTerminalLocalizationDeltaItem>,
    Error
  >([apiUrl], request.instance.get);
  return { data, error, mutate, isLoading: !data && !error };
};

export const updateCodexTerminalLogMode = (id: number, logMode: number) =>
  request.put('/answer/admin/api/codex-terminals/log-mode', {
    id,
    log_mode: logMode,
  });

export const scanCodexTerminalLocalizationDeltas = (hours: number) =>
  request.post<CodexTerminalLocalizationDeltaScanResp>(
    '/answer/admin/api/codex-terminals/localization-deltas/scan',
    { hours },
  );

export const useCodexTerminalLogPolicy = () => {
  const { data, error, mutate } = useSWR<CodexTerminalLogPolicy, Error>(
    ['/answer/admin/api/codex-terminals/log-policy'],
    request.instance.get,
  );
  return { data, error, mutate, isLoading: !data && !error };
};

export const updateCodexTerminalGlobalLogMode = (logMode: number) =>
  request.put('/answer/admin/api/codex-terminals/log-policy', {
    log_mode: logMode,
  });

export const useCloudCodexTestAccessConfig = () => {
  const { data, error, mutate } = useSWR<CloudCodexTestAccessConfig, Error>(
    ['/answer/admin/api/cloud-codex/testing'],
    request.instance.get,
  );
  return { data, error, mutate, isLoading: !data && !error };
};

export const updateCloudCodexTestAccessConfig = (params: {
  testing_enabled: boolean;
  user_ids: string[];
}) =>
  request.put<CloudCodexTestAccessConfig>(
    '/answer/admin/api/cloud-codex/testing',
    { schema_version: 1, ...params },
  );

export const useCodexTerminalLogs = (
  terminalId: number | null,
  page: number,
  pageSize: number,
) => {
  const apiUrl = terminalId
    ? `/answer/admin/api/codex-terminals/logs?${qs.stringify({
        terminal_id: terminalId,
        page,
        page_size: pageSize,
      })}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.ListResult<CodexTerminalLogItem>,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return { data, error, mutate, isLoading: Boolean(apiUrl && !data && !error) };
};

export const codexTerminalLogDownloadURL = (id: number) =>
  `/answer/admin/api/codex-terminals/logs/download?id=${id}`;

export const codexTerminalLogArchiveURL = (terminalId: number) =>
  `/answer/admin/api/codex-terminals/logs/archive?terminal_id=${terminalId}`;

export const codexTerminalRecentLogArchiveURL = (
  params: CodexTerminalRecentLogArchiveParams,
) =>
  `/answer/admin/api/codex-terminals/logs/archive/recent?${qs.stringify(
    params,
    { skipNulls: true },
  )}`;

export const downloadCodexTerminalLog = (id: number) =>
  request.request({
    method: 'GET',
    url: codexTerminalLogDownloadURL(id),
    responseType: 'blob',
    timeout: 0,
  }) as Promise<AxiosResponse<Blob>>;

export const downloadCodexTerminalLogArchive = (terminalId: number) =>
  request.request({
    method: 'GET',
    url: codexTerminalLogArchiveURL(terminalId),
    responseType: 'blob',
    timeout: 0,
  }) as Promise<AxiosResponse<Blob>>;

export const prepareCodexTerminalRecentLogArchive = (
  params: CodexTerminalRecentLogArchiveParams,
  signal?: AbortSignal,
) =>
  request.post<CodexTerminalRecentLogArchiveJob>(
    '/answer/admin/api/codex-terminals/logs/archive/recent/prepare',
    params,
    { signal },
  );

export const getCodexTerminalRecentLogArchiveStatus = (
  jobId: string,
  signal?: AbortSignal,
) =>
  request.get<CodexTerminalRecentLogArchiveJob>(
    `/answer/admin/api/codex-terminals/logs/archive/recent/status?${qs.stringify(
      { job_id: jobId },
    )}`,
    { signal },
  );

export const codexTerminalPreparedLogArchiveURL = (jobId: string) =>
  `/answer/admin/api/codex-terminals/logs/archive/recent/download?${qs.stringify(
    { job_id: jobId },
  )}`;

export const downloadCodexTerminalPreparedLogArchive = (
  jobId: string,
  signal?: AbortSignal,
  onProgress?: (bytes: number) => void,
) =>
  request.request({
    method: 'GET',
    url: codexTerminalPreparedLogArchiveURL(jobId),
    responseType: 'blob',
    timeout: 0,
    signal,
    onDownloadProgress: (event) => onProgress?.(event.loaded),
  }) as Promise<AxiosResponse<Blob>>;

export const assertCodexTerminalZipResponse = async (
  blob: Blob,
  headers: Record<string, string>,
  expectedSize?: number,
) => {
  const contentType =
    headers['content-type'] || headers['Content-Type'] || blob.type;
  assertZipContentType(contentType);
  await assertCompleteZipBlob(blob, expectedSize);
};
